/**
 * particles.js — the animated layer on hero banners.
 *
 * These are hand-written implementations of five effects from particles.js.org
 * rather than the tsParticles library itself. Two reasons: this project vendors
 * every third-party asset into backend/static/vendor and never fetches at build
 * time, and tsParticles is larger than the whole site bundle. The five presets
 * below are small enough to own outright, and owning them is what makes the
 * custom PNG shape and the dashboard-supplied background straightforward.
 *
 * Configuration arrives as a JSON blob on the host element's data attribute,
 * written by whichever renderer drew the hero:
 *
 *   <section class="nv-hero" data-hero-particles='{"effect":"meteors", ...}'>
 *
 * Effects
 *   background_mask  the background shows only through the particles
 *   mouse_particle   drifting field that spawns extra particles under the cursor
 *   meteors          streaks falling across the banner
 *   light_hover      a soft light that follows the pointer and lifts particles
 *   mouse_follow     a trailing comet of particles chasing the pointer
 */

const REDUCED_MOTION = window.matchMedia
    ? window.matchMedia('(prefers-reduced-motion: reduce)')
    : {matches: false};

/** Every canvas currently running, so a route change can stop them all. */
const instances = new Set();

function readConfig(host) {
    const raw = host.getAttribute('data-hero-particles');
    if (!raw) return null;
    try {
        const config = JSON.parse(raw);
        return config && config.effect && config.effect !== 'none' ? config : null;
    } catch (err) {
        return null;
    }
}

function rand(min, max) {
    return min + Math.random() * (max - min);
}

/* ------------------------------------------------------------------ *
 * Particle model
 * ------------------------------------------------------------------ */

function spawn(minX, maxX, height, config, options = {}) {
    let size;
    if (config.size_min && config.size_max) {
        size = rand(config.size_min, config.size_max);
    } else {
        const baseSize = config.size || 6;
        size = rand(baseSize * 0.6, baseSize * 1.4);
    }
    return {
        x: options.x !== undefined ? options.x : rand(minX, maxX),
        y: options.y !== undefined ? options.y : rand(0, height),
        vx: options.vx !== undefined ? options.vx : rand(-0.4, 0.4) * (config.speed || 1),
        vy: options.vy !== undefined ? options.vy : rand(-0.4, 0.4) * (config.speed || 1),
        size: size,
        life: options.life || 0,
        maxLife: options.maxLife || 0,
    };
}

/* ------------------------------------------------------------------ *
 * Engine
 * ------------------------------------------------------------------ */

class HeroParticles {
    constructor(host, config) {
        this.host = host;
        this.config = config;
        this.particles = [];
        this.pointer = {x: -9999, y: -9999, active: false};
        this.frame = null;
        this.sprite = null;

        this.canvas = document.createElement('canvas');
        this.canvas.className = 'nv-hero-particles';
        this.canvas.setAttribute('aria-hidden', 'true');
        this.ctx = this.canvas.getContext('2d');

        const lang = document.documentElement.lang || 'en';
        const wordsRaw = lang === 'fa' ? config.words_fa : config.words_en;
        this.wordsList = wordsRaw ? wordsRaw.split(',').map(w => w.trim()).filter(Boolean) : [];

        const imagesRaw = config.images;
        this.imagesUrlList = imagesRaw ? imagesRaw.split(',').map(i => i.trim()).filter(Boolean) : [];
        this.sprites = [];

        // background_mask paints the banner image through the particle shapes,
        // so the underlying <img> must not also be visible.
        if (config.effect === 'background_mask') {
            host.classList.add('is-particle-masked');
            if (config.background) {
                this.background = new Image();
                this.background.src = config.background;
            }
        }

        host.appendChild(this.canvas);
        this.bind();
        this.resize();
        this.loadSprite();
        this.start();
    }

    loadSprite() {
        this.imagesUrlList.forEach(url => {
            const sprite = new Image();
            sprite.onload = () => this.sprites.push(sprite);
            sprite.src = url;
        });
    }

    bind() {
        this.onResize = () => this.resize();
        this.onMove = (event) => {
            const rect = this.canvas.getBoundingClientRect();
            this.pointer.x = event.clientX - rect.left;
            this.pointer.y = event.clientY - rect.top;
            this.pointer.active = true;
        };
        this.onLeave = () => {
            this.pointer.active = false;
            this.pointer.x = -9999;
            this.pointer.y = -9999;
        };

        window.addEventListener('resize', this.onResize);
        this.host.addEventListener('pointermove', this.onMove);
        this.host.addEventListener('pointerleave', this.onLeave);
    }

    resize() {
        // Cap the device pixel ratio: a 3x buffer on a full-width banner costs
        // more than the visual difference is worth.
        const ratio = Math.min(window.devicePixelRatio || 1, 2);
        const {width, height} = this.host.getBoundingClientRect();
        this.width = width;
        this.height = height;

        const coverage = this.config.coverage || 100;
        const isRTL = document.documentElement.lang === 'fa' || document.documentElement.dir === 'rtl';
        if (coverage < 100) {
            if (isRTL) {
                this.minX = 0;
                this.maxX = width * (coverage / 100);
            } else {
                this.minX = width * ((100 - coverage) / 100);
                this.maxX = width;
            }
        } else {
            this.minX = 0;
            this.maxX = width;
        }
        this.meteorMinX = this.minX === 0 ? -width * 0.3 : this.minX;
        this.meteorMaxX = this.maxX === width ? width * 1.1 : this.maxX;

        this.canvas.width = width * ratio;
        this.canvas.height = height * ratio;
        this.canvas.style.width = `${width}px`;
        this.canvas.style.height = `${height}px`;
        this.ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
        this.seed();
    }

    seed() {
        const count = Math.max(1, this.config.count || 60);
        this.particles = [];
        const hasWords = this.wordsList.length > 0;
        const hasSprites = this.imagesUrlList.length > 0;

        for (let i = 0; i < count; i += 1) {
            const p = this.config.effect === 'meteors'
                ? this.spawnMeteor()
                : spawn(this.minX, this.maxX, this.height, this.config);

            // Mix images and text if both are present
            if (hasWords && hasSprites) {
                p.type = Math.random() > 0.5 ? 'text' : 'image';
            } else if (hasWords) {
                p.type = 'text';
            } else if (hasSprites) {
                p.type = 'image';
            } else {
                p.type = 'circle';
            }

            if (p.type === 'text') {
                p.word = this.wordsList[Math.floor(Math.random() * this.wordsList.length)];
            } else if (p.type === 'image') {
                p.spriteIndex = Math.floor(Math.random() * this.imagesUrlList.length);
            }
            this.particles.push(p);
        }
    }

    spawnMeteor() {
        const speed = this.config.speed || 1;

        let size;
        if (this.config.size_min && this.config.size_max) {
            size = rand(this.config.size_min, this.config.size_max);
        } else {
            size = rand((this.config.size || 6) * 0.4, (this.config.size || 6));
        }

        return {
            // Start off the top edge, spread wide enough that the diagonal
            // travel still covers the right-hand side of the banner.
            x: rand(this.meteorMinX, this.meteorMaxX),
            y: rand(-this.height, 0),
            vx: -rand(2, 4) * speed,
            vy: rand(3, 6) * speed,
            size: size,
            length: rand(60, 160),
        };
    }

    drawShape(particle, alpha) {
        const ctx = this.ctx;
        ctx.globalAlpha = alpha;
        const isNeon = this.config.hero_particle_neon === true || this.config.hero_particle_neon === 'true';

        if (particle.type === 'text' && ['word_cloud', 'floating_text', 'matrix'].includes(this.config.effect)) {
            ctx.fillStyle = this.config.color || '#ffffff';
            ctx.font = `${particle.size * 3}px "Vazirmatn", system-ui, sans-serif`;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            if (isNeon) {
                ctx.shadowColor = this.config.color || '#ffffff';
                ctx.shadowBlur = particle.size * 2;
            }
            ctx.fillText(particle.word, particle.x, particle.y);
            ctx.shadowBlur = 0;
        } else if (particle.type === 'image' && this.sprites.length > 0) {
            const sprite = this.sprites[particle.spriteIndex % this.sprites.length];
            if (sprite) {
                const size = particle.size * 4;
                ctx.drawImage(sprite, particle.x - size / 2, particle.y - size / 2, size, size);
            }
        } else {
            ctx.fillStyle = this.config.color || '#ffffff';
            if (isNeon) {
                ctx.shadowColor = this.config.color || '#ffffff';
                ctx.shadowBlur = particle.size;
            }
            ctx.beginPath();
            ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
            ctx.fill();
            ctx.shadowBlur = 0;
        }
        ctx.globalAlpha = 1;
    }

    wrap(particle) {
        let margin = particle.size;
        if (particle.type === 'text') margin = particle.size * 10;
        else if (particle.type === 'image') margin = particle.size * 3;

        if (particle.x < this.minX - margin) particle.x = this.maxX + margin;
        if (particle.x > this.maxX + margin) particle.x = this.minX - margin;
        if (particle.y < -margin) particle.y = this.height + margin;
        if (particle.y > this.height + margin) particle.y = -margin;
    }

    /* -------------------------------------------------------------- *
     * Effects
     * -------------------------------------------------------------- */

    stepBackgroundMask() {
        const ctx = this.ctx;
        this.particles.forEach((p) => {
            p.x += p.vx;
            p.y += p.vy;
            this.wrap(p);
        });

        // Punch the particle shapes out of a solid fill, then paint the banner
        // image only where those holes are.
        ctx.save();
        ctx.globalCompositeOperation = 'destination-out';
        this.particles.forEach((p) => this.drawShape(p, 1));
        ctx.restore();

        if (this.background && this.background.complete) {
            ctx.save();
            ctx.globalCompositeOperation = 'source-in';
            ctx.drawImage(this.background, 0, 0, this.width, this.height);
            ctx.restore();
        }
    }

    stepMouseParticle() {
        this.particles.forEach((p) => {
            p.x += p.vx;
            p.y += p.vy;
            this.wrap(p);
            this.drawShape(p, this.config.opacity ?? 0.6);
        });

        if (!this.pointer.active) return;
        // Spawn a short-lived particle at the cursor, and retire the oldest so
        // the array cannot grow without bound while the pointer keeps moving.
        this.particles.push(spawn(this.minX, this.maxX, this.height, this.config, {
            x: this.pointer.x,
            y: this.pointer.y,
            vx: rand(-1.2, 1.2),
            vy: rand(-1.2, 1.2),
            maxLife: 60,
            life: 60,
        }));
        if (this.particles.length > (this.config.count || 60) * 2) this.particles.shift();
        this.particles.forEach((p) => {
            if (p.maxLife) p.life -= 1;
        });
        this.particles = this.particles.filter((p) => !p.maxLife || p.life > 0);
    }

    stepMeteors() {
        const ctx = this.ctx;
        const opacity = this.config.opacity ?? 0.6;
        this.particles.forEach((p, index) => {
            p.x += p.vx;
            p.y += p.vy;

            if (this.sprite) {
                this.drawShape(p, opacity);
            } else {
                const tailX = p.x - p.vx * (p.length / 6);
                const tailY = p.y - p.vy * (p.length / 6);
                const gradient = ctx.createLinearGradient(p.x, p.y, tailX, tailY);
                gradient.addColorStop(0, this.config.color || '#ffffff');
                gradient.addColorStop(1, 'transparent');
                ctx.globalAlpha = opacity;
                ctx.strokeStyle = gradient;
                ctx.lineWidth = p.size;
                ctx.lineCap = 'round';
                ctx.beginPath();
                ctx.moveTo(p.x, p.y);
                ctx.lineTo(tailX, tailY);
                ctx.stroke();
                ctx.globalAlpha = 1;
            }

            let margin = p.type === 'image' ? p.size * 3 : p.length;
            if (p.y - margin > this.height || p.x + margin < this.meteorMinX || p.x - margin > this.meteorMaxX) {
                this.particles[index] = this.spawnMeteor();
            }
        });
    }

    stepLightHover() {
        const ctx = this.ctx;
        const opacity = this.config.opacity ?? 0.6;

        if (this.pointer.active) {
            const radius = Math.max(this.width, this.height) * 0.25;
            const glow = ctx.createRadialGradient(
                this.pointer.x, this.pointer.y, 0,
                this.pointer.x, this.pointer.y, radius,
            );
            glow.addColorStop(0, this.config.color || '#ffffff');
            glow.addColorStop(1, 'transparent');
            ctx.globalAlpha = 0.25;
            ctx.fillStyle = glow;
            ctx.fillRect(0, 0, this.width, this.height);
            ctx.globalAlpha = 1;
        }

        this.particles.forEach((p) => {
            p.x += p.vx;
            p.y += p.vy;
            this.wrap(p);

            // Particles near the cursor brighten; the falloff is what makes the
            // light read as a light rather than a flat overlay.
            let alpha = opacity * 0.45;
            if (this.pointer.active) {
                const distance = Math.hypot(p.x - this.pointer.x, p.y - this.pointer.y);
                const reach = Math.max(this.width, this.height) * 0.25;
                if (distance < reach) alpha = opacity * (1 - distance / reach) + opacity * 0.45;
            }
            this.drawShape(p, Math.min(alpha, 1));
        });
    }

    stepMouseFollow() {
        const opacity = this.config.opacity ?? 0.6;
        this.particles.forEach((p, index) => {
            if (this.pointer.active) {
                // Each particle eases toward the pointer at its own rate, which
                // strings them out into a trail instead of a clump.
                const ease = 0.02 + (index / this.particles.length) * 0.05;
                p.x += (this.pointer.x - p.x) * ease * (this.config.speed || 1);
                p.y += (this.pointer.y - p.y) * ease * (this.config.speed || 1);
            } else {
                p.x += p.vx;
                p.y += p.vy;
                this.wrap(p);
            }
            this.drawShape(p, opacity * (1 - index / (this.particles.length * 1.5)));
        });
    }

    stepTextParticles() {
        const opacity = this.config.opacity ?? 0.6;
        this.particles.forEach((p) => {
            if (this.config.effect === 'matrix') {
                p.y += Math.abs(p.vy) + (this.config.speed || 1);
                let margin = p.size;
                if (p.type === 'text') margin = p.size * 10;
                else if (p.type === 'image') margin = p.size * 3;

                if (p.y > this.height + margin) {
                    p.y = -margin;
                    p.x = rand(this.minX, this.maxX);
                }
            } else {
                p.x += p.vx;
                p.y += p.vy;
                this.wrap(p);
            }
            this.drawShape(p, opacity);
        });
    }

    /* -------------------------------------------------------------- *
     * Loop
     * -------------------------------------------------------------- */

    step() {
        const ctx = this.ctx;
        ctx.clearRect(0, 0, this.width, this.height);

        if (this.config.effect === 'background_mask') {
            // The mask needs an opaque base to carve holes out of.
            ctx.fillStyle = 'rgba(0,0,0,1)';
            ctx.fillRect(0, 0, this.width, this.height);
            this.stepBackgroundMask();
        } else if (this.config.effect === 'meteors') {
            this.stepMeteors();
        } else if (this.config.effect === 'light_hover') {
            this.stepLightHover();
        } else if (this.config.effect === 'mouse_follow') {
            this.stepMouseFollow();
        } else if (['word_cloud', 'floating_text', 'matrix'].includes(this.config.effect)) {
            this.stepTextParticles();
        } else {
            this.stepMouseParticle();
        }

        this.frame = requestAnimationFrame(() => this.step());
    }

    start() {
        if (REDUCED_MOTION.matches) {
            // Draw one static frame so the banner is still decorated, then stop.
            this.step();
            cancelAnimationFrame(this.frame);
            this.frame = null;
            return;
        }
        this.step();
    }

    destroy() {
        if (this.frame) cancelAnimationFrame(this.frame);
        window.removeEventListener('resize', this.onResize);
        this.host.removeEventListener('pointermove', this.onMove);
        this.host.removeEventListener('pointerleave', this.onLeave);
        this.host.classList.remove('is-particle-masked');
        this.canvas.remove();
    }
}

/* ------------------------------------------------------------------ *
 * Public API
 * ------------------------------------------------------------------ */

/** Start the configured effect on every hero inside *scope*. */
export function initHeroParticles(scope = document) {
    scope.querySelectorAll('[data-hero-particles]').forEach((host) => {
        const config = readConfig(host);
        if (!config) return;
        instances.add(new HeroParticles(host, config));
    });
}

/** Stop every running effect. Called before the router swaps the page out. */
export function destroyHeroParticles() {
    instances.forEach((instance) => instance.destroy());
    instances.clear();
}
